/**
 * Customer service hold ("Do Not Service") — client-side helpers (#1030).
 *
 * The server is the authority: it decides who may place a hold and who may
 * override one, and it returns 409 SERVICE_HOLD when a held account is booked.
 * These helpers only decide what the UI OFFERS — an override box for a manager,
 * a dead end with "ask a manager" for everyone else — so the two must agree.
 * Mirror of `services/customerServiceHold.ts` on the server.
 */

/** Manager/Admin may place, release, and override a hold. */
export function canManageServiceHold(role: string | null | undefined): boolean {
    const r = (role ?? "").trim().toLowerCase();
    return r === "manager" || r === "admin";
}

export interface ServiceHoldConflict {
    reason: string | null;
    setAt: string | null;
    setByName: string | null;
    canOverride: boolean;
}

/**
 * Pull the hold detail out of a failed API response body. Returns null for
 * anything that isn't a SERVICE_HOLD conflict, so callers can fall through to
 * their normal error handling.
 */
export function parseServiceHoldConflict(body: unknown): ServiceHoldConflict | null {
    if (!body || typeof body !== "object") return null;
    const b = body as Record<string, unknown>;
    if (b.error !== "SERVICE_HOLD") return null;
    const h = (b.serviceHold ?? {}) as Record<string, unknown>;
    return {
        reason: typeof h.reason === "string" ? h.reason : null,
        setAt: typeof h.setAt === "string" ? h.setAt : null,
        setByName: typeof h.setByName === "string" ? h.setByName : null,
        canOverride: h.canOverride === true,
    };
}

/** The line shown at the top of the warning, in the office's own words. */
export function serviceHoldHeadline(reason: string | null | undefined): string {
    const r = (reason ?? "").trim();
    return r ? `On service hold: ${r}` : "On service hold — no reason was recorded.";
}
